import { ApolloClient } from '@apollo/client';
import imageUrlBuilder from '@sanity/image-url';
import { ImageUrlBuilder } from '@sanity/image-url/lib/types/builder';

import { IFeaturedAd, IFeaturedAdClient, IFeaturedAdImage, IGetFeaturedAdsOptions } from '../featured-ad';

import { GET_FEATURED_ADS_QUERY } from './queries/get-featured-ads';

export interface IFeaturedAdResponseImageAsset {
    _id: string;
    url: string;
}

export interface IFeaturedAdResponseImage {
    altText: string;
    asset: IFeaturedAdResponseImageAsset;
    hotspot: {
        x: number;
        y: number;
        height: number;
        width: number;
    };
    crop: {
        top: number;
        bottom: number;
        left: number;
        right: number;
    };
}

export interface IFeaturedAdResponse {
    title: string;
    subtitle: string;
    cta: string;
    image: IFeaturedAdResponseImage;
    url: string;
    publishedAt: Date;
}

export interface IResponseError {
    error: Error;
}

export interface IResponse {
    error: undefined;
    featuredAds: IFeaturedAdResponse[] | null;
}

export class ApolloFeaturedAdClient implements IFeaturedAdClient {
    // eslint-disable-next-line @typescript-eslint/ban-types
    public constructor (private client: ApolloClient<object>) {
    }

    public async fetchAll (options: IGetFeaturedAdsOptions = {}): Promise<IFeaturedAd[]> {
        const { offset, limit }: IGetFeaturedAdsOptions = options;

        try {
            const response = await this.client.query({
                query: GET_FEATURED_ADS_QUERY,
                variables: {
                    offset,
                    limit,
                },
            });

            if (response.errors && response.errors.length > 0) {
                throw response.errors;
            }

            return ApolloFeaturedAdClient.transformFeaturedAds(response.data.allFeaturedAd);
        } catch (error) {
            throw error;
        }
    }

    private static transformFeaturedAds (featuredAds: IFeaturedAdResponse[]): IFeaturedAd[] {
        return featuredAds.map(this.transformFeaturedAdResponse);
    }

    public static transformFeaturedAdResponse (featureAd: IFeaturedAdResponse): IFeaturedAd {
        const { title, subtitle, cta, url, image, publishedAt } = featureAd;
        let transformedImage: undefined | IFeaturedAdImage;

        if (!image || !image.asset) {
            transformedImage = undefined;
        } else {
            const imgUrl16By9: string = ApolloFeaturedAdClient.buildImageUrlFromReference(image, 1440, 810);
            const imgUrl4By3: string = ApolloFeaturedAdClient.buildImageUrlFromReference(image, 704, 528);

            transformedImage = {
                src: image.asset.url,
                alt: image.altText,
                sizes: {
                    '16/9': imgUrl16By9,
                    '4/3': imgUrl4By3,
                },
            };
        }

        return {
            title,
            subtitle,
            cta,
            link: url,
            image: transformedImage,
            publishedAt: new Date(publishedAt),
        };
    }

    private static buildImageUrlFromReference (image: IFeaturedAdResponseImage, width: number, height: number): string {
        const originalImageUrl: string = image.asset.url;
        const [projectId, dataset]: string[] = originalImageUrl.split('cdn.sanity.io/images/')[1].split('/');

        const builder: ImageUrlBuilder = imageUrlBuilder({
            baseUrl: 'https://cdn.sanity.io',
            projectId,
            dataset,
        } as any);

        const imageUrl: ImageUrlBuilder = builder.image(image);

        return imageUrl.height(height).width(width).url();
    }
}
